/**
 * English source strings — the single source of truth for all UI copy.
 *
 * This object's shape is the contract every other locale must satisfy (see
 * `Resources` in `../i18next.d.ts`), and `en` is the i18next fallback, so a key
 * missing from another locale renders the English text rather than the raw key.
 *
 * Keep keys grouped by surface. Product names (MeshGen, SimOps, Diagnosis) are
 * brand identity and stay untranslated across every locale.
 */
const en = {
  common: {
    initialising: 'Initialising...',
    loading: 'Loading...',
    beta: 'Beta',
    authUnavailableTitle: 'Authentication unavailable',
    authUnavailableBody:
      'Secure sign-in could not be initialised. Check your connection and try again.',
    retry: 'Retry',
  },
  nav: {
    home: 'Home',
    diagnosis: 'Diagnosis',
    meshgen: 'MeshGen',
    simops: 'SimOps',
    evaltool: 'EvalTool',
    assistant: 'AI Assistant',
    feedback: 'Feedback',
    whatsNew: "What's new",
    account: 'Account',
    logout: 'Logout',
    // Phrased on the capability, not the mode: `canEndSession` is false for any
    // session that can't be revoked, and naming the demo here would tell the
    // first non-demo one it is a demo.
    logoutUnavailable: "There's no account to sign out of",
  },
  evaltoolLibrary: {
    loadError: 'Failed to load studies',
    emptyTitle: 'Nothing to evaluate yet',
    emptyHint: 'Completed SimOps studies show up here, ready to review.',
  },
  whatsNew: {
    title: "What's new",
    readMore: 'Read more',
    more: '+{{count}} more',
  },
  account: {
    title: 'Account',
    profile: 'Profile',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    theme: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    themeAcademic: 'Academic',
    language: 'Language',
    signOut: 'Sign out',
    signOutDescription: 'End your session on this device.',
    userId: 'User ID',
    usage: {
      title: 'Plan and usage',
      loadError: 'Your plan could not be loaded.',
      usageUnavailable:
        "Usage figures couldn't be loaded. Your plan is shown below.",
      notReadable: "Usage couldn't be loaded.",
      resetsOn: 'Resets on {{date}}.',
      trialEndsOn: 'Trial ends on {{date}}.',
      trialNeedsCard:
        'Add a card in the billing portal to keep access after the trial.',
      noPeriod:
        "Usage isn't being counted yet — this organization has no billing period.",
      unlimited: 'Unlimited',
      notIncluded: 'Not included',
      notCounted: 'Not being counted yet.',
      usedThisPeriod: "You've used {{used}} this period.",
      usedOfAllowance: '{{used}} of {{allowance}}',
      remaining: '{{remaining}} left',
      past: '{{excess}} past what your plan includes',
      limitReached: 'Limit reached',
      extra: 'Extra',
      cost: 'Cost',
      rate: 'Extra billed at {{rate}}.',
      exhaustedTitle: "You've used everything your plan includes",
      exhaustedBodyOwner:
        'Add a payment method to keep going, or wait until the period resets.',
      exhaustedBodyMember:
        "Ask this organization's owner to add a payment method, or wait until the period resets.",
      manage: 'Manage subscription',
      manageOpening: 'Opening…',
      manageFailed: "Couldn't open the billing portal. Try again.",
      status: {
        active: 'Active',
        trialing: 'Trial',
        pastDue: 'Payment failed',
        unpaid: 'Access paused',
        canceled: 'Canceled',
        paused: 'Paused',
      },
      // Per product (ENG-2718): each product has its own count, joined on
      // one line. The agent has no seats — its chat pool is the sum over the
      // seats bought — so it never gets a figure here.
      seatsPurchasedProduct_one: '{{product}}: {{count}} paid seat',
      seatsPurchasedProduct_other: '{{product}}: {{count}} paid seats',
      // The same line when a reduction is booked for the renewal.
      seatsPurchasedProductScheduled_one:
        '{{product}}: {{count}} paid seat, {{scheduled}} from {{date}}',
      seatsPurchasedProductScheduled_other:
        '{{product}}: {{count}} paid seats, {{scheduled}} from {{date}}',
      buy: 'Buy seats',
      changePlan: 'Change plan',
      pendingTitle: 'Payment received',
      pendingBody:
        "We're updating your plan. This usually takes a few seconds.",
      pendingSlowBody:
        'Your plan is taking longer than usual to update. Your payment went through — check back shortly.',
      // The two halves of a failed renewal (ENG-1233). `past_due` is the
      // retry window and loses the customer nothing, so it warns; `unpaid` is
      // after Stripe gave up, and reports access already gone. Neither offers
      // a retry — updating the card in the portal is the only thing that
      // moves either one on.
      //
      // Neither title repeats its status chip's words ("Payment failed",
      // "Access paused"), which sit inches away on the same card: a banner
      // that restates the chip verbatim reads as a rendering bug, and the
      // bodies below carry the actual sentence anyway.
      dunningRetryingTitle: 'Update your card',
      dunningRetryingBody:
        'Your last payment failed. Update your card to keep access.',
      dunningPausedTitle: 'Your subscription is unpaid',
      dunningPausedBody:
        'Access is paused until the open invoice is paid. Update your card and pay it in the billing portal.',
      updateCard: 'Update card',
      cancelledTitle: 'Checkout cancelled',
      cancelledBody: 'Nothing was charged.',
      dismiss: 'Dismiss',
      ownerOnly: "Only this organization's owner can change the subscription.",
      // A downward plan change booked for the next renewal (ENG-2385): one
      // line per product it moves, then the owner's way to call it off. The
      // same note renders above the change form on /checkout.
      pendingChangeTitle: 'Scheduled change',
      pendingChange: '{{product}}: {{current}} until {{date}}, then {{next}}.',
      pendingChangeRemoved:
        '{{product}}: {{current}} until {{date}}, then removed.',
      // The same two when the plan held today has no name here — the product
      // is not held, or its grant names a tier this build does not sell.
      pendingChangeTo: '{{product}}: {{next}} from {{date}}.',
      pendingChangeDropped: '{{product}}: removed from {{date}}.',
      // A seat reduction booked for the renewal (ENG-2718), its own line so
      // a product whose plan stays still says what moves.
      pendingChangeSeats_one: '{{product}}: {{count}} seat from {{date}}.',
      pendingChangeSeats_other: '{{product}}: {{count}} seats from {{date}}.',
      cancelScheduled: 'Cancel scheduled change',
      cancelScheduledPending: 'Cancelling…',
      cancelScheduledFailed:
        "The scheduled change couldn't be cancelled. Try again.",
    },
    workspaces: {
      title: 'Workspaces',
      description:
        'You belong to more than one workspace. Switching reloads the app and signs you in to the one you choose.',
      current: 'Current',
      switch: 'Switch',
      switching: 'Switching…',
      switchError:
        "That workspace couldn't be opened. Try again, or sign out and sign in to it.",
    },
    organization: {
      title: 'Organization',
      loading: 'Loading organization…',
      loadError: 'Organization details could not be loaded.',
      unavailableTitle: 'Organization unavailable',
      unavailableBody:
        'Your account is not linked to an active organization yet.',
      teamLabel: 'Workspace',
      readOnlyTitle: 'Workspace membership',
      readOnlyBody:
        'Only organization owner can manage members and email access domains. Your access remains unchanged.',
      organizationId: 'Organization ID',
      yourRole: 'Your role',
      ownerControls: 'Owner controls',
      statusActive: 'Active',
      statusSuspended: 'Suspended',
      billingBoundaryTitle: 'Membership is not billing',
      billingBoundaryBody:
        'Member changes here do not activate plans or synchronize billable seat quantities. Commercial provisioning remains separate.',
      renameTitle: 'Workspace name',
      renameDescription:
        'Shown to members in the workspace chooser and on invitations.',
      renameLabel: 'Name',
      saveName: 'Save name',
      renamed: 'Workspace name saved.',
      domainsTitle: 'Email access domains',
      domainsDescription:
        'Restrict Stytch email just-in-time access to these domains.',
      domainsOwnershipBoundary:
        'This allowlist does not prove DNS ownership and never creates Khorium membership by itself. Stytch still requires verified email addresses.',
      domainsLabel: 'Allowed domains',
      domainsPlaceholder: 'example.com\nsubsidiary.example',
      domainsEmpty: 'No domains allowed. Email just-in-time access is off.',
      saveDomains: 'Save domains',
      domainsSaved: 'Email access domains saved.',
      membersTitle: 'Members',
      memberCount: '{{count}} members',
      // Seats are per product (ENG-2718): one figure per product the
      // organization holds, and a member is seated in some, all or none.
      seatsProductAssigned: '{{product}} {{assigned}} of {{purchased}}',
      seatsProductAssignedScheduled:
        '{{product}} {{assigned}} of {{purchased}} ({{scheduled}} at renewal)',
      seatsProductUnlimited: '{{product}} {{count}} · no limit',
      seatsUnavailable: 'Seat limit unavailable',
      seatsRetry: 'Retry',
      seatUnassigned: 'No seat',
      assignProductSeat: 'Add {{product}} seat',
      unassignProductSeat: 'Remove {{product}} seat',
      inviteTitle: 'Invite member',
      inviteDescription:
        'Invitations grant team membership after acceptance. Choose app authority separately from resource sharing.',
      emailLabel: 'Email address',
      emailPlaceholder: 'name@company.com',
      inviteRole: 'Organization role',
      invite: 'Send invite',
      inviting: 'Sending…',
      inviteSent: 'Invitation sent to {{email}}.',
      noMembers: 'No members found.',
      you: 'You',
      roleOwner: 'Owner',
      roleAdmin: 'Admin',
      roleMember: 'Member',
      statusInvited: 'Invited',
      changeRole: 'Change role',
      suspend: 'Suspend',
      reactivate: 'Reactivate',
      remove: 'Remove',
      transferOwnership: 'Transfer ownership',
      roleConfirmTitle: 'Change {{email}} to {{role}}?',
      roleConfirmBody:
        'This changes organization authority only. Existing resource shares stay separate.',
      suspendConfirmTitle: 'Suspend {{email}}?',
      suspendConfirmBody:
        'Access stops immediately and owned projects move to current owner. Reactivation restores membership, not prior project ownership.',
      reactivateConfirmTitle: 'Reactivate {{email}}?',
      reactivateConfirmBody:
        'Team access returns with current role. Projects moved during suspension remain with current owner.',
      removeConfirmTitle: 'Remove {{email}}?',
      removeConfirmBody:
        'Membership is deleted and owned projects move to current owner. Comments and existing resource shares remain.',
      transferConfirmTitle: 'Transfer ownership to {{email}}?',
      transferConfirmBody:
        'You become admin and selected member becomes sole owner. Billing remains separate and will not move or activate.',
      confirm: 'Confirm',
      cancel: 'Cancel',
      updating: 'Updating…',
      roleUpdated: 'Role updated.',
      memberSuspended: 'Member suspended.',
      memberReactivated: 'Member reactivated.',
      memberRemoved: 'Member removed.',
      ownershipTransferred: 'Ownership transferred.',
      actionFailed: 'Could not complete action: {{reason}}',
      retry: 'Retry',
    },
  },
  checkout: {
    title: 'Buy seats',
    subtitle:
      'Choose what your organization needs, and how many seats of each.',
    productsLabel: 'What to include',
    productMeshgenBody: 'Generate meshes from your geometry.',
    productSimopsBody: 'Run simulations and studies.',
    productDiagnosisBody: 'Check geometry for problems before meshing.',
    tierNone: 'None',
    tierNoneBody: 'Not included.',
    currentPlan: 'Current plan',
    tierBody: '{{included}} per seat each month · ${{price}} per seat',
    overageNote:
      'Past the included time, work keeps running at ${{rate}} per hour, billed monthly.',
    // Each product has its own seat count (ENG-2718): the control is named
    // for its product, and the total is one line per product plus the sum.
    seatsFor: '{{product}} seats',
    seatsHelp:
      'One seat per person who runs work in that product. You can assign them to members afterwards.',
    lineItem_one: '{{plan}} · {{count}} seat · ${{amount}} per month',
    lineItem_other: '{{plan}} · {{count}} seats · ${{amount}} per month',
    total: '${{amount}} per month',
    trialTotal:
      '{{days}} days free, then ${{amount}} per month — card required.',
    priceNote: "You'll confirm this on the next page before paying.",
    submit: 'Continue to payment',
    submitting: 'Opening checkout…',
    failed: "Checkout couldn't be opened. Nothing was charged.",
    ownerOnly: "Only this organization's owner can buy seats.",
    unavailable: 'Buying seats is not available yet.',
    backToAccount: 'Back to account',
    // Changing an existing plan (ENG-2385). Up is charged today, down waits
    // for the renewal: a product added or moved to a dearer plan is charged
    // the full month's difference now and the hours arrive now; a product
    // dropped or moved to a cheaper plan takes effect at the next renewal,
    // with nothing charged or refunded. The wording lives here so a change of
    // billing policy is a string edit.
    changeTitle: 'Change your plan',
    // The twins of `ownerOnly` and `unavailable`, for the page in its change
    // verb: an admin who followed a link to change the plan must not be
    // refused a purchase they never attempted.
    changeOwnerOnly: "Only this organization's owner can change the plan.",
    changeUnavailable: 'Changing your plan is not available yet.',
    // An `unpaid` org (ENG-1233): the backend refuses both a purchase and a
    // plan change, and the way out is the open invoice in the billing portal.
    changePaused:
      'Your last payment failed and access is paused. Pay the open invoice in the billing portal from your account page before changing the plan.',
    changeSubtitle:
      'Move each product between None, Basic and Plus, and set how many seats each has.',
    changeNote:
      'Moving up or adding seats is charged today and applies straight away. Moving down or removing seats applies at your next renewal, with nothing charged or refunded.',
    changeSubmit: 'Change plan',
    changeSubmitting: 'Changing plan…',
    // What the submit is about to do, once the preview has said. Pay, schedule,
    // or both — the plain `changeSubmit` is for a change that does neither
    // with a figure, such as an upgrade during a trial.
    submitPayToday: 'Pay {{amount}} today',
    submitSchedule: 'Schedule change',
    submitPayAndSchedule: 'Pay {{amount}} today and schedule the rest',
    changeFailed: "Your plan couldn't be changed. Nothing was charged.",
    // Recognised by status, not by the wire's sentence: 402, and 409 while a
    // change is already scheduled.
    cardDeclined:
      'Your card was declined, so nothing changed. Update it in the billing portal and try again.',
    cancelScheduledFirst:
      'A plan change is already scheduled. Cancel it on your account page first.',
    changeDone: 'Plan changed. It can take a moment to show here.',
    changeScheduled:
      'Plan changed. The part moving down takes effect on {{date}}. It can take a moment to show here.',
    changePickAPlan:
      'Pick a plan for every product to see what it costs and when it applies.',
    changeNothingYet:
      'Pick a different plan to see what it costs and when it applies.',
    changeKeepOne:
      'Keep at least one product. To end your subscription, use Manage subscription on your account.',
    currentPlanUnknown:
      "This product's current plan isn't one of these, so choose the one you want.",
    seatsUnbounded: 'No seat limit on this product.',
    changeSeatsInvalid:
      'Give every product between 1 and 100 seats to see what it costs.',
    // The reduction the backend refuses (409): fewer seats than members hold.
    // Said before the round trip, from `seats.by_product`, with the way out.
    seatsBelowAssignedLine:
      '{{product}}: {{assigned}} members hold a seat, {{requested}} requested.',
    seatsBelowAssigned:
      'A product cannot have fewer seats than members hold. Remove seats from members first.',
    manageSeats: 'Manage seats on your account page',
    // The two sentences the preview can produce, either or both. `plans` is
    // the whole set the subscription holds from that date, as `MeshGen Basic,
    // SimOps Plus`.
    chargedToday: 'Charged today: {{amount}}',
    scheduledFrom: 'From {{date}}: {{plans}}',
    scheduledFromUndated: 'From your next renewal: {{plans}}',
    nothingToday: 'Nothing is charged today.',
    previewPending: 'Working out the cost…',
    // No "you can still change your plan" here. The preview and the change
    // refuse on the same terms, so a refusal the owner can actually reach is
    // one the change would repeat — and the button is disabled until the
    // preview has answered. Shown only when the server said nothing usable;
    // its own sentence is preferred.
    previewFailed: "The cost couldn't be worked out just now.",
  },
  home: {
    greetingMorning: 'Good morning,',
    greetingAfternoon: 'Good afternoon,',
    greetingEvening: 'Good evening,',
    fallbackName: 'there',
    importRepair: 'Repair',
    importMesh: 'Mesh',
    newStudy: 'New study',
    noProjects: 'No projects yet. Upload a file to get started.',
    ask: {
      heading: 'What do you want to work on?',
      placeholder: 'Ask about a mesh, a study, or a failure\u2026',
      submit: 'Ask',
      footnote:
        'Your question opens the assistant full screen as a new conversation.',
      dropHint:
        'Drop a CAD part here, or attach one, to ask the assistant about it.',
      dropActive: 'Drop to attach it to your question',
      // Names the part, not the mesh id: the reader recognises the file
      // they uploaded, and the id is what the advisor is handed.
      leadSuggestion: 'Why did {{name}} fail — and should I retry?',
      suggestionFailure: 'Why did my last mesh fail?',
      suggestionSummarise: 'Summarise my running study so far',
      suggestionStudy: 'How do I set up a study from a mesh?',
    },
    work: {
      title: 'Your work',
      onYou: '{{count}} on you',
      review: 'Review',
      reviewRetry: 'Review retry',
      empty: 'Nothing running and nothing waiting on you.',
      error: 'Failed to load your work.',
      partial: 'Some of your work could not be loaded.',
      allActivity: 'All activity',
    },
    recents: {
      title: 'Where you were',
      allMeshes: 'All meshes',
      allStudies: 'All studies',
      filterAll: 'All',
      filterMeshing: 'Meshing',
      filterStudies: 'Studies',
      count: 'last {{count}}',
      countOf: '{{shown}} of {{total}}',
      emptyFilter: 'Nothing recent in this part of the app.',
      error: 'Failed to load recent work.',
      partial: 'Some recent work could not be loaded.',
    },
  },
} as const;

export default en;
